import type { LibraryFilterQuery, LibraryItemsQuery } from "./types";

/** The repeatable list params, in the contract's order. */
const LIST_KEYS = [
  "genre",
  "year",
  "decade",
  "contentRating",
  "studio",
  "resolution",
  "videoRange",
  "audioLanguage",
  "subtitleLanguage",
] as const;

/**
 * The filter half of a library query as wire params (rago-server §4c):
 * one `append` per list value (`?genre=Action&genre=Drama`, OpenAPI
 * form/explode), the three flags, and the trimmed title query. The
 * letters route takes exactly these.
 */
export function libraryFilterSearchParams(
  q: LibraryFilterQuery,
  params: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  if (q.unmatched) params.set("matched", "false");
  if (q.unwatched) params.set("watched", "false");
  if (q.inProgress) params.set("inProgress", "true");
  const text = q.q?.trim();
  if (text) params.set("q", text);
  for (const key of LIST_KEYS) {
    for (const v of q[key] ?? []) {
      if (v) params.append(key, v);
    }
  }
  return params;
}

/** A whole page query: sort, direction, seed, the window, then the filters. */
export function libraryItemsSearchParams(q: LibraryItemsQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.set("sort", q.sort);
  if (q.order) params.set("order", q.order);
  if (q.sort === "random" && q.seed != null) params.set("seed", String(q.seed));
  // The server caps a page at 200; the grid asks for 100-row windows.
  params.set("limit", String(Math.min(Math.max(q.limit ?? 200, 1), 200)));
  params.set("offset", String(Math.max(q.offset ?? 0, 0)));
  return libraryFilterSearchParams(q, params);
}
